import { observable, transaction, makeObservable } from "mobx"
import { BaseStore } from "stores/base.store"
import {
  getAllFormulas,
  getFormulasByGame,
  getFormulaById,
  createFormula,
  updateFormula,
  deleteFormula,
  toggleFormulaActive,
} from "../api/formulas.call"
import { Formula, CreateFormulaDto } from "./types"

export class FormulasStore extends BaseStore {
  @observable
  private _allFormulas: Formula[] = []

  @observable
  private _gameFormulas: Formula[] = []

  @observable
  private _selectedFormula: Formula | null = null

  @observable
  public currentGameId: string | null = null

  @observable
  public selectedFormulaId: string | null = null

  @observable
  public isLoading: boolean = false

  constructor(rootStore: any) {
    super(rootStore)
    makeObservable(this)
  }

  public async getAllFormulas(): Promise<Formula[]> {
    this.resetFuncAfterSuccess()

    const res = await this.defaultFunction(getAllFormulas)

    if (res) {
      transaction(() => {
        this._allFormulas = Array.isArray(res) ? res : []
      })
    }

    return res
  }

  public async getFormulasByGame(gameId: string): Promise<Formula[]> {
    this.resetFuncAfterSuccess()

    const res = await this.defaultFunction(getFormulasByGame, gameId)

    if (res) {
      transaction(() => {
        this._gameFormulas = Array.isArray(res) ? res : []
        this.currentGameId = gameId
      })
    }

    return res
  }

  public async getFormulaById(id: string): Promise<Formula> {
    this.resetFuncAfterSuccess()

    const res = await this.defaultFunction(getFormulaById, id)

    if (res) {
      transaction(() => {
        this._selectedFormula = res
        this.selectedFormulaId = id
      })
    }

    return res
  }

  public clearGameFilter() {
    this.currentGameId = null
    this._gameFormulas = []
  }

  public clearSelectedFormula() {
    this.selectedFormulaId = null
    this._selectedFormula = null
  }

  get allFormulas(): Formula[] {
    return Array.isArray(this._allFormulas) ? this._allFormulas : []
  }

  get gameFormulas(): Formula[] {
    return Array.isArray(this._gameFormulas) ? this._gameFormulas : []
  }

  get selectedFormula(): Formula | null {
    return this._selectedFormula
  }

  get displayedFormulas(): Formula[] {
    if (this.currentGameId !== null) {
      return Array.isArray(this._gameFormulas) ? this._gameFormulas : []
    }

    return Array.isArray(this._allFormulas) ? this._allFormulas : []
  }

  public async createFormula(data: CreateFormulaDto): Promise<Formula> {
    this.resetFuncAfterSuccess()

    this.funcAfterSuccess = async() => {
      if (this.currentGameId) {
        await this.getFormulasByGame(this.currentGameId)
      } else {
        await this.getAllFormulas()
      }
    }

    const res = await this.defaultFunction(createFormula, data)

    return res
  }

  public async updateFormula(id: string, data: CreateFormulaDto): Promise<Formula> {
    this.resetFuncAfterSuccess()

    this.funcAfterSuccess = async() => {
      if (this.currentGameId) {
        await this.getFormulasByGame(this.currentGameId)
      } else {
        await this.getAllFormulas()
      }
    }

    const res = await this.defaultFunction(updateFormula, id, data)

    return res
  }

  public async deleteFormula(id: string): Promise<void> {
    this.resetFuncAfterSuccess()

    this.funcAfterSuccess = async() => {
      if (this.currentGameId) {
        await this.getFormulasByGame(this.currentGameId)
      } else {
        await this.getAllFormulas()
      }
    }

    await this.defaultFunction(deleteFormula, id)
  }

  public async toggleFormulaActive(id: string): Promise<Formula> {
    this.resetFuncAfterSuccess()

    this.funcAfterSuccess = async() => {
      if (this.currentGameId) {
        await this.getFormulasByGame(this.currentGameId)
      } else {
        await this.getAllFormulas()
      }
    }

    const res = await this.defaultFunction(toggleFormulaActive, id)

    return res
  }
}
